import { ChangeDetectionStrategy, Component, inject, Injector, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AllAssessmentStore } from '@features/assessments/stores/all-assessments.store';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AllAssessmentsFilterDialogComponent } from '../all-assessments-filter-dialog/all-assessments-filter-dialog.component';
import { IsRiskManagerDirective } from '@core/directives/is-risk-manager.directive';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-all-assessments-filter',
  imports: [
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonToggleModule,
    MatDialogModule,
    MatTooltipModule,
    IsRiskManagerDirective,
  ],
  templateUrl: './all-assessments-filter.component.html',
  styleUrl: './all-assessments-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllAssessmentsFilterComponent {
  readonly store = inject(AllAssessmentStore);
  readonly dialog = inject(MatDialog);
  readonly injector = inject(Injector);
  // pass ref to dialog to be able inject store inside dialog
  readonly viewContainerRef = inject(ViewContainerRef);

  readonly KEYWORD_SEARCH_TOOLTIP_TEXT = `Keyword search works by finding an exact (case insensitive) match of your input in any of the following:
                  - company;
                  - sector;
                  - country;
                  - project;
                  - sensitivity;
                  - deal ID;
                  - main driver;
                  - underwriting source;
                  - author.`;

  keywordControl = new FormControl('');
  myScopeControl = new FormControl(true, { nonNullable: true });
  myScope = this.store.myScope;

  constructor() {
    this.initScopeControl();
    this.initKeywordControl();
  }

  // can be used only in injection context because of `takeUntilDestroyed`
  private initScopeControl() {
    this.myScopeControl.valueChanges
      .pipe(
        tap(value => this.store.setMyScope(value)),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  // can be used only in injection context because of `takeUntilDestroyed`
  private initKeywordControl() {
    this.keywordControl.valueChanges
      .pipe(
        debounceTime(800),
        distinctUntilChanged(),
        tap(keyword => this.store.setKeywordFilter(keyword?.trim() ?? '')),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  openFilter() {
    const dialogRef = this.dialog.open(AllAssessmentsFilterDialogComponent, {
      viewContainerRef: this.viewContainerRef,
      height: '95%',
      width: '600px',
      position: { right: '1rem' },
    });

    dialogRef.afterClosed().subscribe();
  }
}
